import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';

import type { CreateContentDTO } from '@codes/box-contents';
import { useCreateContent } from '@codes/box-contents';
import { useLoader } from '@codes/loader';

import { useContentCache } from './useContentCache';

import type { ContentView } from '@lemoncloud/eureka-contents-api';

export const useCreateContentWithCache = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { prependContentToCache } = useContentCache();
    const createContent = useCreateContent();
    const { setIsLoading } = useLoader();

    const handleCreate = useCallback(async () => {
        setIsLoading(true);
        const newContent: CreateContentDTO = {
            name: '',
            title: '',
            subject: '',
        };

        try {
            await createContent.mutateAsync(newContent, {
                onSuccess: async (response: ContentView) => {
                    await prependContentToCache(response);
                    navigate(`/box/${response.id}`);
                },
            });
        } catch (e) {
            toast({ description: t('box.generalError'), variant: 'destructive' });
        } finally {
            setIsLoading(false);
        }
    }, [createContent, navigate, prependContentToCache, t]);

    return {
        handleCreate,
        isPending: createContent.isPending,
    };
};
